import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { confirmAction } from '../../components/ConfirmDialog';
import { applicationAPI } from '../../services/api';
import { 
    FaArrowLeft, FaCheckCircle, FaTimesCircle, FaUser, FaEnvelope, 
    FaPhone, FaCalendarAlt, FaMapMarkerAlt, FaGraduationCap, FaFileAlt, 
    FaIdCard, FaBriefcase, FaHeart, FaDollarSign, FaDownload, FaEye,
    FaClock, FaUserCircle, FaVenusMars, FaHome, FaChild, FaBuilding
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { getIdentificationFieldsForCountry } from '../../utils/countryIdentification';

const ApplicationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadApplication();
    }, [id]);

    const loadApplication = async () => {
        try {
            setLoading(true);
            const res = await applicationAPI.getById(id);
            if (res.success && res.application) {
                setApplication(res.application);
            } else {
                toast.error('Application not found');
                navigate(user?.role === 'student_manager' ? '/dashboard/student-manager?tab=applications' : user?.role === 'admin' ? '/dashboard/admin?tab=applications' : '/applications');
            }
        } catch (err) {
            console.error('Failed to load application:', err);
            toast.error('Failed to load application details.');
            navigate(user?.role === 'student_manager' ? '/dashboard/student-manager' : user?.role === 'admin' ? '/dashboard/admin' : '/applications');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (newStatus) => {
        const confirmed = await confirmAction(`Are you sure you want to ${newStatus} this application?`);
        if (!confirmed) return;
        
        try {
            await applicationAPI.updateStatus(id, newStatus, '');
            toast.success('Application status updated successfully!', { duration: 3000 });
            loadApplication();
        } catch (err) {
            console.error('Failed to update status:', err);
            toast.error('Failed to update application status.', { duration: 3000 });
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'selected': return 'bg-green-100 text-green-800 border-green-300';
            case 'rejected': return 'bg-red-100 text-red-800 border-red-300';
            case 'under_review': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'submitted': return 'bg-blue-100 text-blue-800 border-blue-300';
            default: return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    const InfoCard = ({ icon: Icon, label, value, iconColor = "text-primary-600" }) => (
        <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
            <div className={`p-2 rounded-lg bg-white ${iconColor.replace('text-', 'text-')}`}>
                <Icon className={`text-xl ${iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
                <p className="text-sm font-semibold text-gray-900 break-words">{value || 'N/A'}</p>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading application details...</p>
                </div>
            </div>
        );
    }

    if (!application) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600 mb-4">Application not found</p>
                    <Link to={getBackLink()} className="text-blue-600 hover:underline">
                        Back to Applications
                    </Link>
                </div>
            </div>
        );
    }

    const canApproveReject = ['student_manager', 'admin'].includes(user?.role) && 
                             (application.status === 'submitted' || application.status === 'under_review');

    // Determine back link based on user role
    const getBackLink = () => {
        if (user?.role === 'student_manager') {
            return '/dashboard/student-manager?tab=applications';
        }
        if (user?.role === 'admin') {
            return '/dashboard/admin?tab=applications';
        }
        return '/applications';
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Header */}
                <div className="mb-6">
                    <Link 
                        to={getBackLink()}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                    >
                        <FaArrowLeft className="text-sm" />
                        <span className="font-medium">Back to Applications</span>
                    </Link>
                    
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                    Application Details
                                </h1>
                                <p className="text-gray-600 text-lg">
                                    {application.session_name || 'Scholarship Application'}
                                </p>
                            </div>
                            <span className={`px-4 py-2 rounded-lg text-sm font-semibold border ${getStatusColor(application.status)}`}>
                                {application.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                            </span>
                        </div>

                        {/* Action Buttons */}
                        {canApproveReject && (
                            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => handleStatusUpdate('selected')}
                                    className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-sm hover:shadow-md font-medium"
                                >
                                    <FaCheckCircle /> Approve Application
                                </button>
                                <button
                                    onClick={() => handleStatusUpdate('rejected')}
                                    className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-sm hover:shadow-md font-medium"
                                >
                                    <FaTimesCircle /> Reject Application
                                </button>
                                {application.status === 'submitted' && (
                                    <button
                                        onClick={() => handleStatusUpdate('under_review')}
                                        className="flex items-center gap-2 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all shadow-sm hover:shadow-md font-medium"
                                    >
                                        <FaClock /> Mark Under Review
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Personal Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <FaUserCircle className="text-2xl text-blue-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <InfoCard
                            icon={FaUser}
                            label="Full Name"
                            value={`${application.first_name} ${application.last_name || application.surname || ''}`.trim()}
                        />
                        <InfoCard
                            icon={FaEnvelope}
                            label="Email Address"
                            value={application.email}
                        />
                        <InfoCard
                            icon={FaPhone}
                            label="Phone Number"
                            value={application.contact_number || application.phone}
                        />
                        <InfoCard
                            icon={FaCalendarAlt}
                            label="Date of Birth"
                            value={application.date_of_birth ? new Date(application.date_of_birth).toLocaleDateString('en-GB') : 'N/A'}
                        />
                        <InfoCard
                            icon={FaVenusMars}
                            label="Gender"
                            value={application.gender ? application.gender.charAt(0).toUpperCase() + application.gender.slice(1) : 'N/A'}
                        />
                        <InfoCard
                            icon={FaMapMarkerAlt}
                            label="Address"
                            value={application.address_city && application.address_state && application.address_country
                                ? `${application.address_city}, ${application.address_state}, ${application.address_country}`
                                : 'N/A'}
                        />
                        {application.marital_status && (
                            <InfoCard
                                icon={FaHeart}
                                label="Marital Status"
                                value={application.marital_status.charAt(0).toUpperCase() + application.marital_status.slice(1)}
                            />
                        )}
                        {application.number_of_kids !== null && application.number_of_kids !== undefined && (
                            <InfoCard
                                icon={FaChild}
                                label="Number of Kids"
                                value={application.number_of_kids || 0}
                            />
                        )}
                        {application.current_occupation && (
                            <InfoCard
                                icon={FaBriefcase}
                                label="Current Occupation"
                                value={application.current_occupation}
                            />
                        )}
                    </div>
                </div>

                {/* Country-Specific Identification */}
                {application.country_code && application.identification_data && (() => {
                    try {
                        const idData = typeof application.identification_data === 'string' 
                            ? JSON.parse(application.identification_data) 
                            : application.identification_data;
                        const countryInfo = getIdentificationFieldsForCountry(application.country_code);
                        
                        return (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-purple-50 rounded-lg">
                                        <FaIdCard className="text-2xl text-purple-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        Identification Details ({countryInfo.country})
                                    </h2>
                                </div>
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {countryInfo.fields.map((field) => (
                                            <InfoCard
                                                key={field.name}
                                                icon={FaIdCard}
                                                label={field.label}
                                                value={idData[field.name] || 'Not provided'}
                                                iconColor="text-purple-600"
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    } catch (err) {
                        return null;
                    }
                })()}

                {/* Educational Information */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-green-50 rounded-lg">
                            <FaGraduationCap className="text-2xl text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Educational Information</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InfoCard
                            icon={FaGraduationCap}
                            label="Highest Qualification"
                            value={application.highest_qualification}
                        />
                        <InfoCard
                            icon={FaBuilding}
                            label="Institution"
                            value={application.institution_name}
                        />
                        <InfoCard
                            icon={FaCalendarAlt}
                            label="Year of Passing"
                            value={application.year_of_passing}
                        />
                    </div>
                </div>

                {/* Motivation & Career Goals */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                            <FaHeart className="text-2xl text-indigo-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Motivation & Goals</h2>
                    </div>
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                                Motivation
                            </label>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[100px]">
                                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                                    {application.motivation_text || 'Not provided'}
                                </p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                                Career Goals
                            </label>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[100px]">
                                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                                    {application.career_goals || 'Not provided'}
                                </p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                                Financial Background
                            </label>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[100px]">
                                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                                    {application.financial_background || 'Not provided'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Documents */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-orange-50 rounded-lg">
                            <FaFileAlt className="text-2xl text-orange-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {application.government_id_path && (
                            <div className="group border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all bg-white">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-blue-50 rounded-lg">
                                        <FaIdCard className="text-xl text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gray-900 mb-1">Government ID</p>
                                        <p className="text-xs text-gray-500">Identity verification document</p>
                                    </div>
                                </div>
                                <a 
                                    href={`${(import.meta.env.VITE_API_URL || 'http://localhost:5000').replace('/api', '')}/uploads/${application.government_id_path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                                >
                                    <FaEye className="text-sm" /> View Document
                                </a>
                            </div>
                        )}
                        {application.educational_certificates_path && (
                            <div className="group border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition-all bg-white">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-green-50 rounded-lg">
                                        <FaGraduationCap className="text-xl text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gray-900 mb-1">Educational Certificates</p>
                                        <p className="text-xs text-gray-500">Academic credentials</p>
                                    </div>
                                </div>
                                <a 
                                    href={`${(import.meta.env.VITE_API_URL || 'http://localhost:5000').replace('/api', '')}/uploads/${application.educational_certificates_path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                                >
                                    <FaEye className="text-sm" /> View Document
                                </a>
                            </div>
                        )}
                        {application.passport_photo_path && (
                            <div className="group border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all bg-white">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-purple-50 rounded-lg">
                                        <FaUser className="text-xl text-purple-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gray-900 mb-1">Passport Photo</p>
                                        <p className="text-xs text-gray-500">Profile picture</p>
                                    </div>
                                </div>
                                <a 
                                    href={`${(import.meta.env.VITE_API_URL || 'http://localhost:5000').replace('/api', '')}/uploads/${application.passport_photo_path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                                >
                                    <FaEye className="text-sm" /> View Photo
                                </a>
                            </div>
                        )}
                        {!application.government_id_path && !application.educational_certificates_path && !application.passport_photo_path && (
                            <div className="col-span-full text-center py-8 text-gray-500">
                                <FaFileAlt className="text-4xl text-gray-300 mx-auto mb-2" />
                                <p>No documents uploaded</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Application Metadata */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gray-100 rounded-lg">
                            <FaClock className="text-2xl text-gray-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Application Information</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <InfoCard
                            icon={FaFileAlt}
                            label="Application ID"
                            value={`#${application.id}`}
                            iconColor="text-gray-600"
                        />
                        <InfoCard
                            icon={FaClock}
                            label="Stage"
                            value={`Stage ${application.stage || 1} of 4`}
                            iconColor="text-gray-600"
                        />
                        {application.submitted_at && (
                            <InfoCard
                                icon={FaCalendarAlt}
                                label="Submitted At"
                                value={new Date(application.submitted_at).toLocaleString()}
                                iconColor="text-gray-600"
                            />
                        )}
                        {application.updated_at && (
                            <InfoCard
                                icon={FaClock}
                                label="Last Updated"
                                value={new Date(application.updated_at).toLocaleString()}
                                iconColor="text-gray-600"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationDetail;
